(() => {
  if (window.__olivineCartThresholdUpsellBound) return;
  window.__olivineCartThresholdUpsellBound = true;

  const RULES_SELECTOR = '[data-cart-drawer-rules-data]';
  const ROOT_SELECTOR = '[data-cart-threshold-upsell]';
  const LIST_SELECTOR = '[mdle_name="module_6"] .header-group-module-6-bag-products-list';
  const VARIANT_SECTION = 'cart-drawer-upsell-variant';
  const THRESHOLD_PROP = '_threshold_upsell';
  const MAX_RESOURCE_IDS = 50;
  const resourcePayloadCache = new Map();

  const ARROW_BACK =
    '<svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M2.873 8.25L8.56925 13.9462L7.5 15L0 7.5L7.5 0L8.56925 1.05375L2.873 6.75H15V8.25H2.873Z" fill="#000000"/></svg>';
  const ARROW_FORWARD =
    '<svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M12.127 8.25H0V6.75H12.127L6.43075 1.05375L7.5 0L15 7.5L7.5 15L6.43075 13.9462L12.127 8.25Z" fill="#000000"/></svg>';

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const isSafeHandle = (handle) =>
    /^[a-z0-9](?:[a-z0-9_-]{0,254})$/i.test(String(handle || '').trim());

  const sanitizeHref = (value) => {
    if (!value) return '';
    try {
      const url = new URL(String(value).trim(), window.location.origin);
      if (url.origin !== window.location.origin) return '';
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return '';
      if (url.username || url.password) return '';
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return '';
    }
  };

  const isAllowedMediaHost = (hostname) => {
    const host = String(hostname || '').toLowerCase();
    if (!host) return false;
    if (host === window.location.hostname.toLowerCase()) return true;
    return (
      host === 'cdn.shopify.com' ||
      host.endsWith('.shopify.com') ||
      host.endsWith('.shopifycdn.com') ||
      host.endsWith('.shopifycdn.net') ||
      host.endsWith('.myshopify.com')
    );
  };

  const sanitizeMediaUrl = (value) => {
    if (!value) return '';
    try {
      const url = new URL(String(value).trim(), window.location.origin);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return '';
      if (url.username || url.password) return '';
      if (!isAllowedMediaHost(url.hostname)) return '';
      return url.toString();
    } catch {
      return '';
    }
  };

  const cartRoot = () => {
    const raw = window.Shopify?.routes?.root || '/';
    try {
      const url = new URL(raw, window.location.origin);
      if (url.origin !== window.location.origin) return '/';
      const path = url.pathname || '/';
      return path.endsWith('/') ? path : `${path}/`;
    } catch {
      return '/';
    }
  };

  const fetchCart = async () => {
    const response = await fetch(`${cartRoot()}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('No se pudo leer el carrito');
    return response.json();
  };

  const parseRules = () => {
    const nodes = document.querySelectorAll(RULES_SELECTOR);
    if (!nodes.length) return [];

    const byId = new Map();

    nodes.forEach((el) => {
      try {
        const data = JSON.parse(el.textContent || '[]');
        if (!Array.isArray(data)) return;
        data.forEach((rule) => {
          if (!rule || rule.id == null) return;
          const key = String(rule.id);
          const existing = byId.get(key);
          const nextIds = Array.isArray(rule.variantIds) ? rule.variantIds : [];
          const existingIds = Array.isArray(existing?.variantIds) ? existing.variantIds : [];
          if (!existing || nextIds.length > existingIds.length) {
            byId.set(key, rule);
          }
        });
      } catch (error) {
        console.error('cart-threshold-upsell: JSON inválido', error);
      }
    });

    return Array.from(byId.values());
  };

  /** minAmount del Customizer está en moneda local (ej. 3000); cart.total_price viene en centavos. */
  const minAmountToCents = (minAmount) => {
    const major = Number(minAmount);
    if (!Number.isFinite(major) || major < 0) return Number.POSITIVE_INFINITY;
    return Math.round(major * 100);
  };

  const parseResourceIds = (rule) => {
    const ids = Array.isArray(rule?.variantIds) ? rule.variantIds : [];
    const unique = [];
    const seen = new Set();
    ids.forEach((value) => {
      const raw = String(value ?? '').trim();
      const gidMatch = raw.match(/ProductVariant\/(\d+)/i) || raw.match(/Product\/(\d+)/i);
      const id = gidMatch ? gidMatch[1] : raw;
      if (!/^\d+$/.test(id) || seen.has(id) || unique.length >= MAX_RESOURCE_IDS) return;
      seen.add(id);
      unique.push(id);
    });
    return unique;
  };

  const pickMatchingRule = (rules, cartTotalCents) => {
    const matched = (rules || [])
      .filter((rule) => {
        if (!parseResourceIds(rule).length) return false;
        return cartTotalCents >= minAmountToCents(rule.minAmount);
      })
      .sort((a, b) => Number(b.minAmount) - Number(a.minAmount));

    return matched[0] || null;
  };

  const itemResourceIds = (item) => {
    const ids = [];
    const variantId = String(item?.variant_id ?? item?.id ?? '').trim();
    const productId = String(item?.product_id ?? '').trim();
    if (variantId) ids.push(variantId);
    if (productId && productId !== variantId) ids.push(productId);
    return ids;
  };

  const cartItemIds = (cart) => {
    const ids = new Set();
    (cart.items || []).forEach((item) => {
      itemResourceIds(item).forEach((id) => ids.add(id));
    });
    return ids;
  };

  const itemMatchesResourceIds = (item, resourceIds) =>
    itemResourceIds(item).some((id) => resourceIds.has(id));

  const countRuleProductsInCart = (cart, resourceIds) => {
    const ruleIds = new Set((resourceIds || []).map((id) => String(id).trim()).filter(Boolean));
    if (!ruleIds.size) return 0;

    const inCart = new Set();
    (cart.items || []).forEach((item) => {
      if (!itemMatchesResourceIds(item, ruleIds)) return;
      inCart.add(String(item.variant_id ?? item.id ?? item.key ?? '').trim());
    });
    return inCart.size;
  };

  const sectionId = () => window.MODULE_6_CART_SECTION_ID || 'cart-panel-drawer';

  const allRuleResourceIds = (rules) => {
    const ids = new Set();
    (rules || []).forEach((rule) => {
      parseResourceIds(rule).forEach((id) => ids.add(id));
    });
    return ids;
  };

  const isGiftLine = (item) => item?.properties?._is_gift === 'true';

  const isTaggedThresholdLine = (item) => {
    if (!item || isGiftLine(item)) return false;
    return item.properties?.[THRESHOLD_PROP] === 'true';
  };

  const lineMinCents = (item, rules) => {
    const ruleId = String(item?.properties?._threshold_rule_id ?? '').trim();
    if (ruleId) {
      const taggedRule = (rules || []).find((rule) => String(rule.id) === ruleId);
      if (taggedRule) return minAmountToCents(taggedRule.minAmount);
    }

    const minFromLine = Number(item?.properties?._threshold_min);
    if (Number.isFinite(minFromLine) && minFromLine >= 0) {
      return minAmountToCents(minFromLine);
    }

    const matchingMins = (rules || [])
      .filter((rule) => {
        const ids = parseResourceIds(rule);
        return itemResourceIds(item).some((id) => ids.includes(id));
      })
      .map((rule) => minAmountToCents(rule.minAmount));
    if (matchingMins.length) return Math.min(...matchingMins);
    return null;
  };

  const eligibleCartTotal = (cart) =>
    (cart.items || []).reduce((sum, item) => {
      if (isTaggedThresholdLine(item)) return sum;
      return sum + (Number(item.final_line_price) || 0);
    }, 0);

  const changeLineQuantity = async (key, quantity) => {
    const response = await fetch(`${cartRoot()}cart/change.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: key,
        quantity,
        sections: sectionId(),
        sections_url: window.location.pathname,
      }),
    });
    if (!response.ok) throw new Error('No se pudo actualizar la línea del carrito');
    return response.json();
  };

  const dispatchThresholdCartUpdated = (cart) => {
    document.dispatchEvent(
      new CustomEvent('product-added', {
        bubbles: true,
        detail: { ...cart, __thresholdUpsellSynced: true },
      })
    );
  };

  const removeUnqualifiedThresholdItems = async (cart, rules) => {
    if (window.__thresholdUpsellSyncing) return cart;
    if (!cart || !Array.isArray(cart.items) || !cart.items.length) return cart;

    const ruleResourceIds = allRuleResourceIds(rules);
    if (!ruleResourceIds.size) return cart;

    const eligibleTotal = eligibleCartTotal(cart);
    const keysToRemove = cart.items
      .filter((item) => {
        if (!isTaggedThresholdLine(item)) return false;
        const minCents = lineMinCents(item, rules);
        if (minCents == null) return false;
        return eligibleTotal < minCents;
      })
      .map((item) => item.key)
      .filter(Boolean);

    if (!keysToRemove.length) return cart;

    window.__thresholdUpsellSyncing = true;
    try {
      let latest = cart;
      for (const key of keysToRemove) {
        latest = await changeLineQuantity(key, 0);
      }
      dispatchThresholdCartUpdated(latest);
      return latest;
    } finally {
      window.__thresholdUpsellSyncing = false;
    }
  };

  const idsMatch = (left, right) => String(left ?? '').trim() === String(right ?? '').trim();

  const featuredImageUrl = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value.src || value.url || '';
  };

  const sanitizeVariantPayload = (variant) => {
    const id = Number(variant?.id);
    if (!Number.isFinite(id) || id <= 0) return null;
    return {
      id,
      available: variant?.available === true,
      price: Number(variant?.price) || 0,
      compare_at_price: Number(variant?.compare_at_price) || 0,
    };
  };

  const normalizeProductPayload = (data) => {
    if (!data || typeof data !== 'object') return null;
    const variantId = Number(data.variantId);
    if (!Number.isFinite(variantId) || variantId <= 0) return null;

    const variants = Array.isArray(data.variants)
      ? data.variants.map(sanitizeVariantPayload).filter(Boolean)
      : [];
    const fallbackVariant = sanitizeVariantPayload({
      id: variantId,
      available: data.available === true,
      price: data.price,
      compare_at_price: data.compareAtPrice,
    });
    const safeVariants = variants.length ? variants : fallbackVariant ? [fallbackVariant] : [];
    if (!safeVariants.length) return null;

    const handle = isSafeHandle(data.handle) ? String(data.handle).trim() : '';
    const matchedVariant = safeVariants.find((item) => item.id === variantId);

    return {
      id: data.id != null ? data.id : '',
      title: String(data.title || ''),
      handle,
      url: typeof data.url === 'string' ? data.url : handle ? `/products/${handle}` : '',
      featuredImage: data.featuredImage || null,
      available: data.available === true && matchedVariant?.available === true,
      variantId,
      price: Number(data.price) || 0,
      compareAtPrice: Number(data.compareAtPrice) || 0,
      variants: matchedVariant ? [matchedVariant] : safeVariants.slice(0, 1),
    };
  };

  const parseSectionPayload = (text) => {
    if (!text) return null;
    try {
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const el = doc.querySelector('[data-cart-drawer-variant-data]');
      if (!el) return null;
      const data = JSON.parse(el.textContent || 'null');
      return normalizeProductPayload(data);
    } catch {
      return null;
    }
  };

  const mapProductJsToPayload = (product) => {
    if (!product || product.id == null) return null;
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const variant = variants.find((item) => item?.available) || variants[0];
    if (!variant || variant.id == null) return null;
    const image =
      featuredImageUrl(variant.featured_image) ||
      featuredImageUrl(product.featured_image) ||
      featuredImageUrl(Array.isArray(product.images) ? product.images[0] : '');
    return normalizeProductPayload({
      id: product.id,
      title: product.title || '',
      handle: product.handle || '',
      url: product.url || '',
      featuredImage: image || null,
      available: variant.available === true,
      variantId: variant.id,
      price: variant.price,
      compareAtPrice: variant.compare_at_price,
      variants: [
        {
          id: variant.id,
          available: variant.available === true,
          price: variant.price,
          compare_at_price: variant.compare_at_price,
        },
      ],
    });
  };

  const fetchVariantSectionPayload = async (variantId, root) => {
    const response = await fetch(
      `${root}variants/${encodeURIComponent(variantId)}/?section_id=${VARIANT_SECTION}`,
      { credentials: 'same-origin' }
    );
    if (!response.ok) return null;
    return parseSectionPayload(await response.text());
  };

  const fetchProductJs = async (handle, root) => {
    const safe = String(handle || '').trim();
    if (!/^\d+$/.test(safe) && !isSafeHandle(safe)) return null;
    const response = await fetch(`${root}products/${encodeURIComponent(safe)}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  const resolveProductById = async (productId, root) => {
    const numericProduct = await fetchProductJs(productId, root);
    if (numericProduct && idsMatch(numericProduct.id, productId)) return numericProduct;

    const suggestUrl = `${root}search/suggest.json?q=${encodeURIComponent(
      `id:${productId}`
    )}&resources[type]=product&resources[limit]=4&resources[options][unavailable_products]=last`;

    const response = await fetch(suggestUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;
    let data;
    try {
      data = await response.json();
    } catch {
      return null;
    }
    const products = data?.resources?.results?.products;
    if (!Array.isArray(products)) return null;
    const match = products.find(
      (item) => idsMatch(item?.id, productId) || String(item?.id ?? '').endsWith(`/${productId}`)
    );
    if (!match?.handle) return null;
    const product = await fetchProductJs(match.handle, root);
    if (!product || !idsMatch(product.id, productId)) return null;
    return product;
  };

  const fetchSearchProductPayload = async (productId, root) => {
    const response = await fetch(
      `${root}search?q=${encodeURIComponent(`id:${productId}`)}&type=product&section_id=${VARIANT_SECTION}`,
      { credentials: 'same-origin' }
    );
    if (!response.ok) return null;
    const data = parseSectionPayload(await response.text());
    if (!data || !idsMatch(data.id, productId)) return null;
    return data;
  };

  const fetchResourcePayload = (resourceId) => {
    const key = String(resourceId);
    if (resourcePayloadCache.has(key)) return resourcePayloadCache.get(key);

    let root = cartRoot();
    if (!root.endsWith('/')) root += '/';

    const request = (async () => {
      try {
        const variantData = await fetchVariantSectionPayload(key, root);
        if (variantData && idsMatch(variantData.variantId, key)) return variantData;

        const product = await resolveProductById(key, root);
        const fromProduct = mapProductJsToPayload(product);
        if (fromProduct) return fromProduct;

        return fetchSearchProductPayload(key, root);
      } catch {
        return null;
      }
    })().then((data) => {
      if (!data) resourcePayloadCache.delete(key);
      return data;
    });

    resourcePayloadCache.set(key, request);
    return request;
  };

  const getMaxPurchases = (rule) => {
    const raw = Number(rule?.maxPurchases);
    if (!Number.isFinite(raw) || raw < 1) return 1;
    return Math.min(10, Math.floor(raw));
  };

  const ensureRoot = () => {
    let root =
      document.querySelector(`[mdle_name="module_6"] ${ROOT_SELECTOR}`) ||
      document.querySelector(ROOT_SELECTOR);

    if (root) return root;

    const list = document.querySelector(LIST_SELECTOR);
    if (!list) return null;

    root = document.createElement('div');
    root.className = 'header-group-module-6-bag-gwp header-group-module-6-bag-threshold';
    root.setAttribute('data-cart-threshold-upsell', '');
    root.hidden = true;

    const recommendations = list.querySelector('[data-cart-recommendations]');
    const gwp = list.querySelector('[data-cart-gwp]');
    if (recommendations) {
      list.insertBefore(root, recommendations);
    } else if (gwp && gwp.nextSibling) {
      list.insertBefore(root, gwp.nextSibling);
    } else if (gwp) {
      gwp.after(root);
    } else {
      list.appendChild(root);
    }

    return root;
  };

  const buildSlide = (product, rule) => {
    const url = sanitizeHref(product.url || (product.handle ? `/products/${product.handle}` : ''));
    const title = escapeHtml(product.title || '');
    const imageSrc = sanitizeMediaUrl(product.featuredImage);
    const variantId = Number(product.variantId) || 0;
    const available =
      product.available === true &&
      variantId > 0 &&
      String(variantId) === String(product.variantId).trim();
    const price = Number(product.price) || 0;
    const compareAt = Number(product.compareAtPrice) || 0;

    const variantsPayload = Array.isArray(product.variants)
      ? product.variants.map(sanitizeVariantPayload).filter(Boolean)
      : [];
    const variantsJson = escapeHtml(
      JSON.stringify(
        variantsPayload.length
          ? variantsPayload
          : [
              {
                id: variantId,
                available: true,
                price,
                compare_at_price: compareAt,
              },
            ]
      )
    );
    const propertiesJson = escapeHtml(
      JSON.stringify({
        _threshold_upsell: 'true',
        _threshold_rule_id: String(rule?.id ?? ''),
        _threshold_min: String(rule?.minAmount ?? ''),
      })
    );

    const atcHtml = available
      ? `<add-to-cart
					class="header-group-module-6-routine-item-atc"
					variant-id="${variantId}"
					quantity="1"
					data-variants='${variantsJson}'
					data-properties='${propertiesJson}'
				>
					<button type="button" btwn class="header-group-module-6-routine-item-button">Agregar</button>
				</add-to-cart>`
      : `<button type="button" class="header-group-module-6-routine-item-button" disabled>Agotado</button>`;

    return `
			<article class="header-group-module-6-routine-item" data-threshold-product-id="${escapeHtml(product.variantId || product.id)}">
				${
          url
            ? `<a class="header-group-module-6-routine-item-media" href="${escapeHtml(url)}">
							${
                imageSrc
                  ? `<img class="header-group-module-6-routine-item-img" src="${escapeHtml(imageSrc)}" alt="${title}" width="113" height="113" loading="lazy">`
                  : ''
              }
						</a>`
            : `<div class="header-group-module-6-routine-item-media">
							${
                imageSrc
                  ? `<img class="header-group-module-6-routine-item-img" src="${escapeHtml(imageSrc)}" alt="${title}" width="113" height="113" loading="lazy">`
                  : ''
              }
						</div>`
        }
				<div class="header-group-module-6-routine-item-body">
					<div class="header-group-module-6-routine-item-copy">
						${
              url
                ? `<a href="${escapeHtml(url)}" class="header-group-module-6-routine-item-title-link">
								<span class="header-group-module-6-routine-item-title">${title}</span>
							</a>`
                : `<span class="header-group-module-6-routine-item-title">${title}</span>`
            }
						${atcHtml}
					</div>
				</div>
			</article>
		`;
  };

  const buildMarkup = (rule, products) => {
    const title = escapeHtml(rule.title || 'Productos desbloqueados');
    const slides = products.map((product) => buildSlide(product, rule)).filter(Boolean).join('');
    if (!slides) return '';

    return `
			<div class="header-group-module-6-bag-slider-wrapper header-group-module-6-bag-slider-wrapper--gwp header-group-module-6-bag-slider-wrapper--threshold">
				<div class="header-group-module-6-bag-slider-header-wrapper">
					<h3 class="header-group-module-6-bag-slider-title">${title}</h3>
					<div class="header-group-module-6-bag-slider-buttons">
						<button type="button" class="header-group-module-6-bag-slider-button a1" aria-label="Anterior" disabled>
							<span class="header-group-module-6-bag-slider-button__icon" aria-hidden="true">${ARROW_BACK}</span>
						</button>
						<button type="button" class="header-group-module-6-bag-slider-button a2" aria-label="Siguiente">
							<span class="header-group-module-6-bag-slider-button__icon" aria-hidden="true">${ARROW_FORWARD}</span>
						</button>
					</div>
				</div>
				<div class="header-group-module-6-bag-slider-slider-wrapper header-group-module-6-routine-track" data-routine-track>
					<div class="header-group-module-6-routine-list">${slides}</div>
				</div>
			</div>
		`;
  };

  const clearRoot = (root) => {
    if (!root) return;
    root.hidden = true;
    root.innerHTML = '';
    root.removeAttribute('data-threshold-rule-id');
    root.removeAttribute('data-threshold-fingerprint');
  };

  let refreshTimer = 0;
  let refreshing = false;
  let refreshQueued = false;
  let skipContentChangeUntil = 0;

  const refreshCartThresholdUpsell = async () => {
    if (!ensureRoot()) return;

    const liveRoot = () => ensureRoot();

    const rules = parseRules();
    if (!rules.length) {
      clearRoot(liveRoot());
      return;
    }

    let cart;
    try {
      cart = await fetchCart();
    } catch (error) {
      console.error(error);
      clearRoot(liveRoot());
      return;
    }

    cart = await removeUnqualifiedThresholdItems(cart, rules);

    if (!cart.item_count) {
      clearRoot(liveRoot());
      return;
    }

    const eligibleTotal = eligibleCartTotal(cart);
    const rule = pickMatchingRule(rules, eligibleTotal);
    if (!rule) {
      clearRoot(liveRoot());
      return;
    }

    const resourceIds = parseResourceIds(rule);
    const maxPurchases = getMaxPurchases(rule);
    const purchasedFromList = countRuleProductsInCart(cart, resourceIds);
    if (purchasedFromList >= maxPurchases) {
      clearRoot(liveRoot());
      return;
    }

    const inCartIds = cartItemIds(cart);
    const idsToFetch = resourceIds.filter((id) => !inCartIds.has(id));
    const payloads = await Promise.all(idsToFetch.map((id) => fetchResourcePayload(id)));
    const products = payloads.filter((product) => {
      const variantId = String(product?.variantId ?? '').trim();
      if (!variantId || Number(product.variantId) <= 0) return false;
      if (product.available !== true) return false;
      const productId = String(product.id ?? '').trim();
      if (inCartIds.has(variantId) || (productId && inCartIds.has(productId))) return false;
      return true;
    });

    const root = liveRoot();
    if (!root) return;

    if (!products.length) {
      clearRoot(root);
      return;
    }

    const markup = buildMarkup(rule, products);
    if (!markup) {
      clearRoot(root);
      return;
    }

    const nextRuleId = String(rule.id || '');
    const fingerprint = `${nextRuleId}:${maxPurchases}:${purchasedFromList}:${products
      .map((p) => `${p.id}-${p.variantId}`)
      .join(',')}`;
    if (root.dataset.thresholdFingerprint === fingerprint && !root.hidden) {
      return;
    }

    root.dataset.thresholdRuleId = nextRuleId;
    root.dataset.thresholdFingerprint = fingerprint;
    root.innerHTML = markup;
    root.hidden = false;
    skipContentChangeUntil = Date.now() + 250;
    window.dispatchEvent(new CustomEvent('header-module:content-change'));
  };

  const scheduleRefresh = () => {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(() => {
      if (refreshing) {
        refreshQueued = true;
        return;
      }
      refreshing = true;
      refreshCartThresholdUpsell()
        .catch(() => {})
        .finally(() => {
          refreshing = false;
          if (refreshQueued) {
            refreshQueued = false;
            scheduleRefresh();
          }
        });
    }, 120);
  };

  window.refreshCartThresholdUpsell = () => scheduleRefresh();

  document.addEventListener('product-added', () => {
    scheduleRefresh();
  });

  document.addEventListener('cart:updated', () => {
    scheduleRefresh();
  });

  // El panel reescribe el DOM al refrescar; reintentamos cuando el mount vuelve a existir.
  window.addEventListener('header-module:content-change', () => {
    if (Date.now() < skipContentChangeUntil) return;
    scheduleRefresh();
  });

  const boot = () => {
    scheduleRefresh();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
